import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";
import QueryTypeBadge from "./components/QueryTypeBadge";
import StatusBadge from "./components/StatusBadge";

function useDebounce(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const IST_LOCALE_OPTS = {
  timeZone: "Asia/Kolkata",
  dateStyle: "medium",
  timeStyle: "short",
};

function formatCreatedAt(val) {
  if (val == null || val === "") return "—";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString("en-IN", IST_LOCALE_OPTS) + " IST";
  } catch {
    return String(val);
  }
}

const EMPTY_CREATE_FORM = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  pnr: "",
  query_type: "",
  description: "",
};

export default function TicketsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [status, setStatus] = useState("");
  const [queryType, setQueryType] = useState("");
  const [needsRefundRecovery, setNeedsRefundRecovery] = useState(false);
  const [recoveryPendingCount, setRecoveryPendingCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const fetchTickets = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;
    const recoveryFilter =
      opts.needsRefundRecovery !== undefined
        ? opts.needsRefundRecovery
        : needsRefundRecovery;

    setLoading(true);
    setErr("");
    try {
      const res = await api.post("/admin/tickets/list", {
        search: debouncedSearch || undefined,
        status: status || undefined,
        query_type: queryType || undefined,
        ...(recoveryFilter ? { needs_refund_recovery: 1 } : {}),
        page: nextPage,
        limit: nextLimit,
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
      setRecoveryPendingCount(Number(res.data?.recovery_pending_count) || 0);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e.message ||
        "Could not load tickets. Please try again.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch only when `page` changes; filters call `fetchTickets` explicitly

  useEffect(() => {
    setPage(1);
    fetchTickets({ page: 1 });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
    fetchTickets({ page: 1 });
  }, [status, queryType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!createOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setCreateOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createOpen]);

  const goPrev = () => {
    if (page <= 1) return;
    setPage((p) => p - 1);
  };

  const goNext = () => {
    const totalCount = meta.total ?? 0;
    if (page * limit >= totalCount) return;
    setPage((p) => p + 1);
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 20;
    setLimit(v);
    setPage(1);
    fetchTickets({ page: 1, limit: v });
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setQueryType("");
    setNeedsRefundRecovery(false);
    setPage(1);
    fetchTickets({ page: 1, needsRefundRecovery: false });
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    if (createSubmitting) return;
    setCreateOpen(false);
    setCreateForm(EMPTY_CREATE_FORM);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const email = createForm.customer_email.trim();
    const qt = createForm.query_type;
    if (!email || !qt) {
      toast.error("Customer email and query type are required.");
      return;
    }

    setCreateSubmitting(true);
    try {
      await api.post("/admin/tickets", {
        customer_name: createForm.customer_name.trim() || undefined,
        customer_email: email,
        customer_phone: createForm.customer_phone.trim() || undefined,
        pnr: createForm.pnr.trim() || undefined,
        query_type: qt,
        description: createForm.description.trim() || undefined,
      });
      toast.success("Ticket created successfully");
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
      await fetchTickets({ page: meta.page, limit });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to create ticket";
      toast.error(msg);
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openView = (ticket) => {
    if (!ticket?.ticket_id) return;
    navigate(`/admin/tickets/${ticket.ticket_id}`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <h1 className="text-2xl font-bold">Tickets</h1>
          {recoveryPendingCount > 0 ? (
            <button
              type="button"
              onClick={() => {
                setNeedsRefundRecovery(true);
                setPage(1);
                fetchTickets({ page: 1, needsRefundRecovery: true });
              }}
              className="ml-0 sm:ml-2 inline-flex items-center rounded-full bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-1 hover:bg-red-200 border border-red-200"
            >
              ⚠️ {recoveryPendingCount} need recovery
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openCreateModal}
            className="rounded-xl bg-black text-white px-4 py-2 text-sm font-semibold hover:bg-gray-900"
          >
            Create Ticket
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end gap-3 border rounded-2xl p-4">
        <div className="lg:min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
            placeholder="Ticket ID / PNR / Email / Zoho Ref..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="lg:min-w-[200px]">
          <label className="text-sm font-semibold">Status</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="Open">Open</option>
            <option value="On Hold">On Hold</option>
            <option value="Escalated">Escalated</option>
            <option value="Closed">Closed</option>
          </select>
        </div>

        <div className="lg:min-w-[200px]">
          <label className="text-sm font-semibold">Query Type</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
            value={queryType}
            onChange={(e) => setQueryType(e.target.value)}
          >
            <option value="">All</option>
            <option value="REFUND">Refund</option>
            <option value="RESCHEDULING">Rescheduling</option>
            <option value="CANCELLATION">Cancellation</option>
            <option value="BOOKING_FAILED">Booking Failed</option>
          </select>
        </div>

        <div className="lg:min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span>Ticket List</span>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={page === 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>

            <button
              onClick={goNext}
              disabled={(page * limit >= (meta.total ?? 0)) || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No tickets found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Ticket ID</th>
                <th className="px-4 py-3">Customer Name</th>
                <th className="px-4 py-3">PNR</th>
                <th className="px-4 py-3">Query Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Assigned Agent</th>
                <th className="px-4 py-3">Created At</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="border-t">
              {rows.map((row) => (
                <tr
                  key={row.ticket_id}
                  className={`border-t hover:bg-gray-50 ${
                    Number(row.needs_refund_recovery) === 1
                      ? "bg-red-50/40 border-l-4 border-l-red-500"
                      : ""
                  }`}
                >
                  <td className="px-4 py-3 font-semibold">{row.ticket_id ?? "—"}</td>
                  <td className="px-4 py-3">
                    {row.customer_name ??
                      [row.first_name, row.last_name].filter(Boolean).join(" ") ??
                      "—"}
                  </td>
                  <td className="px-4 py-3">{row.pnr ?? "—"}</td>
                  <td className="px-4 py-3">
                    <QueryTypeBadge queryType={row.query_type} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {Number(row.needs_refund_recovery) === 1 ? (
                        <span
                          className="text-red-600 shrink-0"
                          title="Needs refund recovery"
                          aria-label="Needs refund recovery"
                        >
                          ⚠️
                        </span>
                      ) : null}
                      <StatusBadge status={row.status} />
                    </div>
                  </td>
                  <td className="px-4 py-3">{row.assigned_agent ?? row.assigned_agent_name ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatCreatedAt(row.created_at ?? row.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openView(row)}
                      >
                        View Ticket
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-red-600 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                        onClick={async () => {
                          const id = row.ticket_id;
                          if (id == null) return;
                          if (
                            !window.confirm(
                              `Are you sure you want to delete Ticket #${id}? This action cannot be undone.`
                            )
                          ) {
                            return;
                          }
                          try {
                            await api.post("/admin/tickets/delete", {
                              ticket_id: id,
                            });
                            toast.success("Ticket deleted successfully.");
                            await fetchTickets({ page: meta.page, limit });
                          } catch (e) {
                            toast.error(
                              e?.response?.data?.message ||
                                e.message ||
                                "Could not delete the ticket. Please try again."
                            );
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {createOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCreateModal();
          }}
        >
          <div
            className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border bg-white shadow-lg"
            role="dialog"
            aria-labelledby="create-ticket-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="border-b px-5 py-4">
              <h2 id="create-ticket-title" className="text-lg font-bold">
                Create New Ticket
              </h2>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Customer Name</label>
                <input
                  type="text"
                  className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                  value={createForm.customer_name}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, customer_name: e.target.value }))
                  }
                  placeholder="Optional"
                  disabled={createSubmitting}
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Customer Email <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  required
                  className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                  value={createForm.customer_email}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, customer_email: e.target.value }))
                  }
                  disabled={createSubmitting}
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">Customer Phone</label>
                <input
                  type="text"
                  className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                  value={createForm.customer_phone}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, customer_phone: e.target.value }))
                  }
                  placeholder="Optional"
                  disabled={createSubmitting}
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">PNR</label>
                <input
                  type="text"
                  className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                  value={createForm.pnr}
                  onChange={(e) => setCreateForm((f) => ({ ...f, pnr: e.target.value }))}
                  placeholder="Optional"
                  disabled={createSubmitting}
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">
                  Query Type <span className="text-red-600">*</span>
                </label>
                <select
                  required
                  className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                  value={createForm.query_type}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, query_type: e.target.value }))
                  }
                  disabled={createSubmitting}
                >
                  <option value="">Select query type</option>
                  <option value="REFUND">Refund</option>
                  <option value="RESCHEDULING">Rescheduling</option>
                  <option value="CANCELLATION">Cancellation</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700">Description</label>
                <textarea
                  rows={4}
                  className="mt-2 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black resize-y min-h-[100px]"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="Optional"
                  disabled={createSubmitting}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  disabled={createSubmitting}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    createSubmitting ||
                    !createForm.customer_email.trim() ||
                    !createForm.query_type
                  }
                  className="rounded-xl bg-black text-white px-4 py-2 text-sm font-semibold hover:bg-gray-900 disabled:opacity-50 min-w-[120px]"
                >
                  {createSubmitting ? "Creating…" : "Create Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
